// TES-1093: „Mein Tag" — rollenspezifisches Dashboard für tester.
// Owner-Matrix (2026-06-11): tester bekommt die „Heute"-Preview nach dem
// Inspector-Dashboard-Muster (Quick-Actions, Open-Defects-Panel, Recent-
// Inspections, MPP-Woche) plus die persönliche Tagessicht (meine überfälligen/
// heute fälligen Prüfungen). Wiederverwendet S1-Bausteine (TES-954):
// DashboardBento, StatCard, KpiStatCard — Daten user-scoped (canSeeAll=false),
// RLS bleibt die Sicherheitsgrenze.
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { format } from "date-fns";
import {
  AlertTriangle,
  CalendarCheck,
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Play,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/components/layout/PageContainer";
import { DashboardBento } from "@/components/dashboard/DashboardBento";
import { DashboardSkeleton } from "@/components/dashboard/DashboardSkeleton";
import {
  DashboardQuickActions,
  RoleDashboardHeader,
  RoleKpiRow,
  RoleTileSection,
  type RoleKpiItem,
} from "@/components/dashboard/RoleDashboardSections";
import {
  inspectionAssetName,
  makeFormatters,
  resolveDateLocale,
} from "@/components/dashboard/dashboardBento.helpers";
import {
  countOpenDefects,
  getInspectionDisplayStatus,
  type MyDayInspection,
  type MyDayView,
} from "@/components/dashboard/roleDashboard.helpers";
import { useMyDayQuery } from "@/hooks/useMyDayQuery";
import { useRoleDashboardData } from "@/hooks/useRoleDashboardData";
import { isDateOverdue, DATE_FORMATS } from "@/lib/dateUtils";
import { cn } from "@/lib/utils";

/** Persönliche Tages-KPIs (Überfällig / Heute fällig / In Bearbeitung / Offen). */
function buildTesterKpis(myDay: MyDayView, t: TFunction): RoleKpiItem[] {
  return [
    {
      testId: "tester-kpi-overdue",
      label: t("dashboard.overdue"),
      value: myDay.overdueCount,
      icon: Clock,
      tone: "error",
    },
    {
      testId: "tester-kpi-due-today",
      label: t("dashboard.tester.dueToday"),
      value: myDay.dueTodayCount,
      icon: CalendarCheck,
      tone: "secondary",
    },
    {
      testId: "tester-kpi-in-progress",
      label: t("dashboard.inProgress"),
      value: myDay.inProgressCount,
      icon: Play,
      tone: "primary",
    },
    {
      testId: "tester-kpi-open",
      label: t("dashboard.tester.openTotal"),
      value: myDay.openTotal,
      icon: ClipboardList,
      tone: "primary",
    },
  ];
}

/** Fälligkeits-/Status-Label einer anstehenden Prüfung (überfällig > in Bearbeitung > Due-Chip). */
function upcomingRowLabel(
  inspection: MyDayInspection,
  t: TFunction,
  dueChip: (iso: string | null) => string,
): string {
  if (isDateOverdue(inspection.due_date)) return t("dashboard.overdue");
  if (getInspectionDisplayStatus(inspection) === "in_progress") return t("dashboard.inProgress");
  return dueChip(inspection.due_date);
}

/** Akzent-Klassen einer Zeile (überfällig = destruktiv, sonst Safety-Yellow). */
function upcomingRowClasses(overdue: boolean): { border: string; label: string } {
  if (overdue) return { border: "border-destructive", label: "text-destructive" };
  return { border: "border-secondary", label: "text-secondary-foreground" };
}

/** Eine Zeile „anstehende Prüfung" — border-l-4-Akzent wie das Open-Defects-Panel (screen-04). */
function UpcomingInspectionRow({
  inspection,
  onOpen,
  dueChip,
}: {
  inspection: MyDayInspection;
  onOpen: () => void;
  dueChip: (iso: string | null) => string;
}) {
  const { t } = useTranslation();
  const classes = upcomingRowClasses(isDateOverdue(inspection.due_date));

  return (
    <button
      type="button"
      data-testid={`tester-upcoming-row-${inspection.id}`}
      onClick={onOpen}
      className={cn(
        "w-full p-4 bg-surface rounded border-l-4 flex justify-between items-center gap-3 group cursor-pointer hover:bg-surface-container-low transition-colors text-left",
        classes.border,
      )}
    >
      <div className="min-w-0">
        <p className={cn("font-bold text-label-caps", classes.label)}>
          {upcomingRowLabel(inspection, t, dueChip)}
        </p>
        <p className="text-sm text-foreground mt-1 truncate">
          {inspectionAssetName(inspection, t("dashboard.unknownChecklist"))}
        </p>
        {inspection.checklists?.name && (
          <p className="text-data-tabular text-muted-foreground mt-0.5 truncate">
            {inspection.checklists.name}
          </p>
        )}
      </div>
      <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-1 transition-transform shrink-0" />
    </button>
  );
}

/** Panel „Meine anstehenden Prüfungen" — überfällige zuerst, dann nach Fälligkeit. */
function UpcomingInspectionsPanel({ myDay }: { myDay: MyDayView }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { formatDueChip } = makeFormatters(i18n.language);
  const dueChip = (iso: string | null) => formatDueChip(iso, t("dashboard.bento.mppNoDueDate"));

  return (
    <div
      className="bg-surface-container-lowest border border-outline-variant rounded p-6"
      data-testid="tester-upcoming"
    >
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-primary" />
          {t("dashboard.tester.upcoming")}
        </h3>
        <Button
          variant="link"
          onClick={() => navigate("/inspections")}
          className="text-primary font-bold text-label-caps p-0 h-auto hover:underline"
        >
          {t("dashboard.tester.viewAll")}
        </Button>
      </div>
      {myDay.upcoming.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center py-10 text-muted-foreground"
          data-testid="tester-upcoming-empty"
        >
          <ClipboardCheck className="h-10 w-10 mb-2 text-success" />
          <p className="text-sm">{t("dashboard.tester.upcomingEmpty")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {myDay.upcoming.map((inspection) => (
            <UpcomingInspectionRow
              key={inspection.id}
              inspection={inspection}
              onOpen={() => navigate(`/inspections/${inspection.id}`)}
              dueChip={dueChip}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function TesterDashboard() {
  const { t, i18n } = useTranslation();
  const dateLocale = resolveDateLocale(i18n.language);
  const {
    userId,
    userName,
    stats,
    recentInspections,
    criticalDefects,
    showSkeleton,
    inspectionTiles,
    defectTiles,
  } = useRoleDashboardData({ includeUnassigned: true });
  const { view: myDay } = useMyDayQuery(userId);

  return (
    <PageContainer className="space-y-8" data-testid="tester-dashboard">
      {/* Header — Mobile bekommt den Hero-Stack im Bento (screen-09) */}
      <RoleDashboardHeader
        userName={userName}
        title={t("dashboard.tester.title")}
        subtitle={format(new Date(), DATE_FORMATS.DATE_LONG, { locale: dateLocale })}
        actions={<DashboardQuickActions />}
      />

      {/* Mein Tag — persönliche KPI-Zeile */}
      <RoleKpiRow items={buildTesterKpis(myDay, t)} testId="tester-kpi-row" />

      <UpcomingInspectionsPanel myDay={myDay} />

      {showSkeleton ? (
        <DashboardSkeleton />
      ) : (
        <>
          {/* Inspector-Preview-Kern: Open Defects + Recent Inspections + MPP-Woche (user-scoped) */}
          <DashboardBento
            recentInspections={recentInspections}
            criticalDefects={criticalDefects}
            stats={stats}
            defectsOpen={countOpenDefects(stats)}
            getDisplayStatus={getInspectionDisplayStatus}
          />

          <RoleTileSection
            title={t("dashboard.inspections")}
            icon={ClipboardList}
            iconClassName="h-5 w-5 text-primary"
            tiles={inspectionTiles}
            dataTour="dashboard-inspections"
          />

          <RoleTileSection
            title={t("dashboard.defects")}
            icon={AlertTriangle}
            iconClassName="h-5 w-5 text-warning"
            tiles={defectTiles}
            dataTour="dashboard-defects"
          />
        </>
      )}
    </PageContainer>
  );
}
